import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Loader } from '@ferris/react/components/loader';

import LocaleRoute from './LocaleRoute';
import { fetchAuthUrlWithService } from '../../utils/AuthUrlUtil';
import { UserCtx } from '../../contexts/user';

export const AUTH_FLOW_TYPES = {
    VR: 'VRBO',
    ADD_PROPERTY: 'ADD_PROPERTY',
    UPL: 'UPL',
    // added this auth flow type to not redirect the user needlessly
    // QOL: have the UPL instead of redirecting to AAP flow
    // do the eligibility check. Then there will be no need for Agentic type
    // but that is out of scope for OBCF-4182
    AGENTIC: 'AGENTIC'
};

const redirectFunction = (vrboOwnerAuthUrl) => {
    if (typeof window === 'object') {
        // TODO: Update me to reference pageflow instead of using hardcoded value
        const authUrl = fetchAuthUrlWithService(vrboOwnerAuthUrl, window.location.pathname);
        window.location.assign(`${authUrl}&screen=login&treatment=2fa`);
    }
};

const epcRedirectFunction = () => {
    if (typeof window === 'object') {
        let redirectUrl = `/Account/Logon?returnUrl=${encodeURIComponent(window.location.href)}`;
        if (window.location.host.includes('local')) {
            redirectUrl = `https://www.expediapartnercentral.com.integration.sb.karmalab.net${redirectUrl}`;
        } else {
            redirectUrl = `${window.location.protocol}//${(window.location.hostname).replace('apps', 'www')}${redirectUrl}`;
        }
        window.location.assign(redirectUrl);
    }
};

const aapRejectFunction = (locale) => {
    if (typeof window === 'object') {
        const redirectUrl = `/${locale}/list/add-property/ineligible`;
        window.location.assign(redirectUrl);
    }
};

const addPropertyRedirectFunction = (locale) => {
    if (typeof window === 'object') {
        const redirectUrl = `/${locale}/list/eligibility/add-property`;
        window.location.assign(redirectUrl);
    }
};

const authorized = ({
    protectedType,
    hasAdvertiserForSite
}) => {
    // default to owner.
    const pt = protectedType || 'owner';

    if (pt === 'owner' && !hasAdvertiserForSite) {
        return false; // They are effectively a traveler in this case
    }
    // in all other cases we are authorized, because we are logged in as an owner.
    return true;
};

const ProtectedRouteProps = {
    children: PropTypes.element.isRequired,
    vrboOwnerAuthUrl: PropTypes.string.isRequired,
    isLoggedIn: PropTypes.bool,
    locale: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    experiments: PropTypes.arrayOf(PropTypes.object).isRequired,
    authFlow: PropTypes.string
};

function VrAuthFn({ children, ...props }) {
    if (!authorized(props)) {
        return (
            <LocaleRoute {...props} render={(redirectFunction(props.vrboOwnerAuthUrl))} />
        );
    }
    return (
        <LocaleRoute {...props}>
            {children}
        </LocaleRoute>
    );
}

VrAuthFn.propTypes = { ...ProtectedRouteProps };

function AapAuthFn({ children, ...props }) {
    const {
        getAapUserInformation
    } = useContext(UserCtx);
    const [userInformation, setUserInformation] = React.useState(null);
    const retrieveEpcUserInformation = async () => {
        const information = await getAapUserInformation();
        setUserInformation(information);
    };
    useEffect(() => {
        retrieveEpcUserInformation();
    }, []);

    if (!userInformation) {
        return (
            <Loader
                slide
                size="normal"
                visible
                title=""
            />
        );
    }

    if (!userInformation.epcUserId) {
        return (
            <LocaleRoute {...props} render={(epcRedirectFunction())} />
        );
    }
    if (!userInformation.aapEligibility) {
        return (
            <LocaleRoute {...props} render={(aapRejectFunction(props.locale))} />
        );
    }
    return (
        <LocaleRoute {...props}>
            {children}
        </LocaleRoute>
    );
}

AapAuthFn.propTypes = { ...ProtectedRouteProps };

function UplAuthFn({ children, ...props }) {
    const {
        getBasicUserInformation
    } = useContext(UserCtx);
    const [userInformation, setUserInformation] = React.useState(null);
    const retrieveEpcUserInformation = async () => {
        const information = await getBasicUserInformation();
        setUserInformation(information);
    };
    useEffect(() => {
        retrieveEpcUserInformation();
    }, []);

    // this code block waits until the user information is fetched
    if (!userInformation) {
        return (
            <Loader
                slide
                size="normal"
                visible
                title=""
            />
        );
    }

    // this code block redirects to EPC if the user is not logged in
    if (!userInformation.epcUserId) {
        return (
            <LocaleRoute {...props} render={(epcRedirectFunction())} />
        );
    }

    // users with existing properties should be redirected to add property flow
    if (userInformation.hasExistingProperties) {
        return (
            <LocaleRoute {...props} render={(addPropertyRedirectFunction(props.locale))} />
        );
    }

    return (
        <LocaleRoute {...props}>
            {children}
        </LocaleRoute>
    );
}

UplAuthFn.propTypes = { ...ProtectedRouteProps };

function AgenticAuthFn({ children, ...props }) {
    const {
        getAapUserInformation
    } = useContext(UserCtx);
    const [userInformation, setUserInformation] = React.useState(null);
    const retrieveEpcUserInformation = async () => {
        const information = await getAapUserInformation();
        setUserInformation(information);
    };
    useEffect(() => {
        retrieveEpcUserInformation();
    }, []);

    // this code block waits until the user information is fetched
    if (!userInformation) {
        return (
            <Loader
                slide
                size="normal"
                visible
                title=""
            />
        );
    }

    // this code block redirects to EPC if the user is not logged in
    if (!userInformation.epcUserId) {
        return (
            <LocaleRoute {...props} render={(epcRedirectFunction())} />
        );
    }

    // users with existing properties should be redirected to add property flow
    if (userInformation.hasExistingProperties && !userInformation.aapEligibility) {
        return (
            <LocaleRoute {...props} render={(aapRejectFunction(props.locale))} />
        );
    }

    return (
        <LocaleRoute {...props}>
            {children}
        </LocaleRoute>
    );
}

AgenticAuthFn.propTypes = { ...ProtectedRouteProps };

function ProtectedRoute({ children, ...props }) {
    const { authFlow } = props;

    switch (authFlow) {
    case AUTH_FLOW_TYPES.VR:
        return <VrAuthFn {...props}>{children}</VrAuthFn>;
    case AUTH_FLOW_TYPES.ADD_PROPERTY:
        return <AapAuthFn {...props}>{children}</AapAuthFn>;
    case AUTH_FLOW_TYPES.UPL:
        return <UplAuthFn {...props}>{children}</UplAuthFn>;
    case AUTH_FLOW_TYPES.AGENTIC:
        return <AgenticAuthFn {...props}>{children}</AgenticAuthFn>;
    default:
        return null;
    }
}

ProtectedRoute.propTypes = ProtectedRouteProps;

ProtectedRoute.defaultProps = {
    isLoggedIn: false,
    locale: 'en_US',
    authFlow: AUTH_FLOW_TYPES.VR
};

export default ProtectedRoute;
